import { useMemo } from 'react';
import { Briefcase, FileText, Users, Clock, TrendingUp, Check, Sparkles } from 'lucide-react';
import { 
  ACTIVE_PIPELINE_STATUSES, 
  TERMINAL_STATUSES, 
  matchesStatus 
} from '../constants/submissionStatuses';

// Icon mapping to resolve references from constants
const ICON_MAP = {
  FileText,
  Users,
  Clock,
  TrendingUp,
  Check,
  Briefcase,
  Sparkles
};

/**
 * useRecruiterMetrics
 * 
 * Calculates all metrics for recruiter dashboard (non-Account Manager users).
 * Includes:
 * - Current Open Jobs
 * - Current Pipeline Metrics (10 tiles: 5 active + 5 terminal)
 * - Activity Metrics (time-filtered)
 * 
 * Key Features:
 * - Filters by active jobs only (not filled/cancelled)
 * - Filters by jobs added in last 6 months (excludes stale data)
 * - Holistic client view (all submissions for assigned clients)
 * 
 * @param {Object} params
 * @returns {Object} - All calculated metrics
 */
export const useRecruiterMetrics = ({
  jobs,
  submissions,
  candidates,
  selectedUser,
  team,
  allUsers,
  allowedClients,
  selectedClients,
  selectedTimeframe,
  viewMode,
  isLeadRecruiter
}) => {
  
  // Constants for job and submission statuses
  const TERMINAL_JOB_STATUSES = ['filled', 'cancelled', 'lost', 'declined', 'on_hold'];
  const ACTIVE_JOB_STATUSES = ['accepting_candidates', 'covered', 'interviewing', 'offer_out'];
  const ACTION_REQUIRED_JOB_STATUSES = ['accepting_candidates', 'interviewing'];

  // ============================================================================
  // RECRUITER SUBMISSIONS (NO candidate ownership check)
  // ============================================================================
  // CRITICAL RULE: Submissions visibility is NOT filtered by candidate ownership
  // Only filter by: job's client access + job status (handled in pipeline metrics)
  // 
  // Business Rule: Any recruiter can submit any candidate to any job they have access to
  // See: /app/CRITICAL_RULES.md - Submissions section
  // ============================================================================
  const recruiterSubmissions = useMemo(() => {
    // Simply return all submissions - no candidate ownership filtering
    // Job-based filtering happens in currentPipelineMetrics (by client access)
    return submissions;
  }, [submissions]);

  // ============================================================================
  // TIME-FILTERED SUBMISSIONS
  // ============================================================================
  const getDateRange = () => {
    const now = new Date();
    const currentYear = now.getFullYear();
    
    switch (selectedTimeframe) {
      case 'ytd':
        return { start: new Date(currentYear, 0, 1), end: now };
      case 'this_month':
        return { start: new Date(currentYear, now.getMonth(), 1), end: now };
      case 'last_month':
        const lastMonth = new Date(currentYear, now.getMonth() - 1, 1);
        const lastMonthEnd = new Date(currentYear, now.getMonth(), 0);
        return { start: lastMonth, end: lastMonthEnd };
      case 'last_6_months':
        const sixMonthsAgo = new Date(now);
        sixMonthsAgo.setMonth(now.getMonth() - 6);
        return { start: sixMonthsAgo, end: now };
      case 'last_12_months':
        const twelveMonthsAgo = new Date(now);
        twelveMonthsAgo.setMonth(now.getMonth() - 12);
        return { start: twelveMonthsAgo, end: now };
      default:
        return { start: new Date(currentYear, 0, 1), end: now };
    }
  };

  const filteredRecruiterSubmissions = useMemo(() => {
    const { start, end } = getDateRange();
    
    return recruiterSubmissions.filter(sub => {
      // CRITICAL: Use date_added first, fallback to submission_date, then created_at
      // This ensures AI Sourcing submissions (which have submission_date) are included
      const dateStr = sub.date_added || sub.submission_date || sub.created_at;
      if (!dateStr) return false;
      
      const subDate = new Date(dateStr);
      if (isNaN(subDate.getTime())) return false; // Invalid date
      
      return subDate >= start && subDate <= end;
    });
  }, [recruiterSubmissions, selectedTimeframe]);

  // ============================================================================
  // CURRENT OPEN JOBS
  // ============================================================================
  const currentOpenJobsMetric = useMemo(() => {
    const sixMonthsAgo = new Date();
    sixMonthsAgo.setMonth(sixMonthsAgo.getMonth() - 6);
    
    const currentOpenJobs = jobs.filter(job => {
      const status = (job.status || '').toLowerCase();
      const needsAction = ACTION_REQUIRED_JOB_STATUSES.includes(status);
      
      if (!needsAction) return false;
      
      const jobClient = job.client_company;
      if (!jobClient || !allowedClients.includes(jobClient)) return false;
      
      if (selectedClients.size > 0 && !selectedClients.has(jobClient)) return false;
      
      // Use date_added or fall back to created_at
      const dateAdded = job.date_added || job.created_at;
      if (!dateAdded) return true; // Include jobs without dates
      
      try {
        const jobDate = new Date(dateAdded);
        // Check if date is valid
        if (isNaN(jobDate.getTime())) return true; // Include invalid dates
        if (jobDate < sixMonthsAgo) return false;
      } catch (error) {
        // If date parsing fails, include the job anyway
        return true;
      }
      
      return true;
    });

    return {
      count: currentOpenJobs.length,
      data: currentOpenJobs,
      label: 'Current Open Jobs',
      description: 'Jobs needing submissions (last 6 months)',
      color: 'indigo',
      icon: Briefcase
    };
  }, [jobs, allowedClients, ACTION_REQUIRED_JOB_STATUSES, selectedClients]);

  // ============================================================================
  // CURRENT PIPELINE METRICS (10 tiles - HOLISTIC CLIENT VIEW)
  // ============================================================================
  const currentPipelineMetrics = useMemo(() => {
    // Filter jobs added in last 6 months to avoid stale data
    const sixMonthsAgo = new Date();
    sixMonthsAgo.setMonth(sixMonthsAgo.getMonth() - 6);
    
    const isJobActive = (jobId) => {
      const job = jobs.find(j => j.id === jobId);
      if (!job) return false;
      
      // Check job status is active (not terminal like filled/cancelled)
      const status = (job.status || '').toLowerCase();
      if (!ACTIVE_JOB_STATUSES.includes(status)) return false;
      
      // Check job was added in last 6 months to exclude stale data
      const dateAdded = job.date_added;
      if (!dateAdded) return false;
      const jobDate = new Date(dateAdded);
      if (jobDate < sixMonthsAgo) return false;
      
      // Check job is for allowed client
      const jobClient = job.client_company;
      if (!jobClient || !allowedClients.includes(jobClient)) return false;
      
      // Apply client filter if selected
      if (selectedClients.size > 0 && job.client_company) {
        return selectedClients.has(job.client_company);
      }
      
      return true;
    };

    // HOLISTIC VIEW - ALL submissions for allowed clients (not just recruiter-owned)
    // Only for active jobs added in last 6 months
    const allActiveJobSubmissions = submissions.filter(sub => isJobActive(sub.job_id));

    // Build metrics object dynamically from status definitions
    const metrics = {};
    
    // Process active pipeline statuses
    ACTIVE_PIPELINE_STATUSES.forEach(statusDef => {
      const matchingSubmissions = allActiveJobSubmissions.filter(sub => 
        matchesStatus(sub, statusDef)
      );
      
      // Map icon name to actual component
      let IconComponent = Check; // default
      if (statusDef.key === 'submission') IconComponent = FileText;
      else if (statusDef.key === 'clientSubmission') IconComponent = Users;
      else if (statusDef.key === 'interview') IconComponent = Clock;
      else if (statusDef.key === 'offerExtended') IconComponent = TrendingUp;
      else if (statusDef.key === 'offerAccepted') IconComponent = Check;
      else if (statusDef.key === 'onboardingInitiated') IconComponent = Check;
      else if (statusDef.key === 'onboarded') IconComponent = Check;
      else if (statusDef.key === 'rejected') IconComponent = FileText;
      else if (statusDef.key === 'interviewRejected') IconComponent = Clock;
      else if (statusDef.key === 'offerRejected') IconComponent = TrendingUp;
      
      metrics[statusDef.key] = {
        count: matchingSubmissions.length,
        data: matchingSubmissions,
        label: statusDef.label,
        description: statusDef.description,
        color: statusDef.color,
        icon: IconComponent
      };
    });
    
    // Process terminal statuses
    TERMINAL_STATUSES.forEach(statusDef => {
      const matchingSubmissions = allActiveJobSubmissions.filter(sub => 
        matchesStatus(sub, statusDef)
      );
      
      // Map icon name to actual component
      let IconComponent = Check; // default
      if (statusDef.key === 'submission') IconComponent = FileText;
      else if (statusDef.key === 'clientSubmission') IconComponent = Users;
      else if (statusDef.key === 'interview') IconComponent = Clock;
      else if (statusDef.key === 'offerExtended') IconComponent = TrendingUp;
      else if (statusDef.key === 'offerAccepted') IconComponent = Check;
      else if (statusDef.key === 'onboardingInitiated') IconComponent = Check;
      else if (statusDef.key === 'onboarded') IconComponent = Check;
      else if (statusDef.key === 'rejected') IconComponent = FileText;
      else if (statusDef.key === 'interviewRejected') IconComponent = Clock;
      else if (statusDef.key === 'offerRejected') IconComponent = TrendingUp;
      
      metrics[statusDef.key] = {
        count: matchingSubmissions.length,
        data: matchingSubmissions,
        label: statusDef.label,
        description: statusDef.description,
        color: statusDef.color,
        icon: IconComponent
      };
    });

    return metrics;
  }, [submissions, jobs, ACTIVE_JOB_STATUSES, selectedClients, allowedClients]);

  // ============================================================================
  // ACTIVITY METRICS (time-filtered, unique jobs)
  // ============================================================================
  const activityMetrics = useMemo(() => {
    const uniqueJobIdsSubmitted = new Set(filteredRecruiterSubmissions.map(sub => sub.job_id));
    const jobsSubmittedTo = Array.from(uniqueJobIdsSubmitted)
      .map(jobId => jobs.find(j => j.id === jobId))
      .filter(job => {
        if (!job) return false;
        if (selectedClients.size > 0 && job.client_company) {
          return selectedClients.has(job.client_company);
        }
        return true;
      });

    const onboardedSubmissions = filteredRecruiterSubmissions.filter(sub => 
      sub.status === 'Onboarded'
    );
    const uniqueJobIdsFilled = new Set(onboardedSubmissions.map(sub => sub.job_id));
    const jobsFilled = Array.from(uniqueJobIdsFilled)
      .map(jobId => jobs.find(j => j.id === jobId))
      .filter(job => {
        if (!job) return false;
        if (selectedClients.size > 0 && job.client_company) {
          return selectedClients.has(job.client_company);
        }
        return true;
      });

    // AI Sourcing metrics
    const aiSourcedCandidates = candidates.filter(c => 
      c.source === 'ai_sourcing' || c.created_via === 'ai_sourcing'
    );
    
    const internalCandidates = candidates.filter(c => 
      c.source !== 'ai_sourcing' && c.created_via !== 'ai_sourcing'
    );

    return {
      jobsSubmittedTo: {
        count: jobsSubmittedTo.length,
        data: jobsSubmittedTo,
        label: 'Jobs Submitted To',
        description: 'Unique jobs with submissions',
        color: 'blue',
        icon: Briefcase
      },
      jobsFilled: {
        count: jobsFilled.length,
        data: jobsFilled,
        label: 'Jobs Successfully Placed',
        description: 'Jobs with onboarded candidates',
        color: 'green',
        icon: Check
      },
      aiSourcedCandidates: {
        count: aiSourcedCandidates.length,
        data: aiSourcedCandidates,
        label: 'AI Sourced Candidates',
        description: 'Candidates found via AI sourcing',
        color: 'purple',
        icon: 'Sparkles'
      },
      internalCandidates: {
        count: internalCandidates.length,
        data: internalCandidates,
        label: 'Internal Candidates',
        description: 'Manually added candidates',
        color: 'gray',
        icon: Users
      }
    };
  }, [filteredRecruiterSubmissions, jobs, selectedClients, candidates]);

  return {
    currentOpenJobsMetric,
    currentPipelineMetrics,
    activityMetrics
  };
};

export default useRecruiterMetrics;
